import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { deleteQuotation } from '../api';
import useConfirmDialog from '../hooks/useConfirmDialog';
import useOpenQuotations from '../hooks/useOpenQuotations';
import { notify } from '../utils/alertBus';
import DocumentTabs from './DocumentTabs';

const PendingQuotationTabs = ({
  branchId,
  activeQuotationId,
  activeQuotationLiveId = null,
  activeQuotationLiveTotal = null,
  onSelect,
  enableRealtime = true,
  tone = 'neutral',
  canDelete = true,
  canCreate = true,
  canSelect = true,
}) => {
  const { quotations, loading, error, reload } = useOpenQuotations(branchId, {
    enableRealtime,
  });
  const { confirm } = useConfirmDialog();

  const displayQuotations = useMemo(() => {
    const liveQuotationId = activeQuotationLiveId == null ? null : String(activeQuotationLiveId);
    const liveTotal = Number(activeQuotationLiveTotal);

    return (quotations || []).map((quotation) => ({
      ...quotation,
      ...(
        liveQuotationId !== null
        && String(quotation?.id) === liveQuotationId
        && Number.isFinite(liveTotal)
          ? { totalAmount: liveTotal }
          : {}
      ),
    }));
  }, [activeQuotationLiveId, activeQuotationLiveTotal, quotations]);

  const handleSelect = (quotation) => {
    if (!canSelect) return;
    onSelect?.(quotation?.id || null);
  };

  const handleNew = () => {
    if (!canCreate) return;
    onSelect?.(null);
  };

  const handleDelete = async (quotation) => {
    if (!canDelete || !branchId || !quotation?.id) return;
    const approved = await confirm({
      title: 'Eliminar borrador',
      message: `¿Eliminar la cotización #${quotation.id}?`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;

    try {
      await deleteQuotation(branchId, quotation.id);
      if (String(quotation.id) === String(activeQuotationId)) {
        onSelect?.(null, { skipSave: true });
      }
      await reload();
    } catch (err) {
      console.error('Error deleting quotation draft:', err);
      notify(err?.response?.data?.message || 'No se pudo eliminar la cotización.');
    }
  };

  const formatLabel = (quotation) => {
    const clientName = quotation.Client?.name || 'Detalle sin cliente';
    const total =
      typeof quotation.totalAmount === 'number'
        ? `$${quotation.totalAmount.toLocaleString('de-DE')}`
        : '';

    let time = '';
    if (quotation.updatedAt || quotation.createdAt) {
      const updated = new Date(quotation.updatedAt || quotation.createdAt);
      if (!Number.isNaN(updated.getTime())) {
        time = format(updated, 'HH:mm');
      }
    }

    return [clientName, total || null, time || null]
      .filter(Boolean)
      .join(' · ');
  };

  const buildTabTitle = (quotation) => `Cot. #${quotation.id}`;

  return (
    <DocumentTabs
      tabs={displayQuotations}
      activeId={activeQuotationId}
      activeNew={!activeQuotationId}
      onSelect={handleSelect}
      onNew={handleNew}
      onDelete={handleDelete}
      canCreate={canCreate}
      canSelect={canSelect}
      canDelete={canDelete}
      loading={loading}
      error={error}
      scopeReady
      tone={tone}
      newTitle="Nueva cotización"
      listTitle="Ver cotizaciones abiertas"
      menuTitle="Cotizaciones abiertas"
      emptyMessage="No hay cotizaciones abiertas."
      getTabTitle={buildTabTitle}
      getTabMeta={formatLabel}
    />
  );
};

export default PendingQuotationTabs;
